/**
 * Section shadowing feedback rubric — the config-driven field schema that
 * defines what a peer reviewer fills in when shadowing a section.
 *
 * This file holds the BUILT-IN default rubric (v1). At runtime the live rubric
 * is served from Firestore — the current version inline on the
 * `sectionShadow/{courseId}` config doc, superseded versions under its
 * `rubricVersionHistory` subcollection — and admins edit it there. The built-in
 * here seeds that store and is the fallback (`getBuiltInShadowRubric`) when
 * Firestore has no rubric for a version yet. Each `shadowFeedback` document
 * stores the `rubricVersion` id it was written against, so historical feedback
 * resolves its fields (via that version) without a per-document snapshot.
 *
 * This mirrors the teacher-feedback field-descriptor pattern
 * (`FeedbackOutputField` in
 * `firebase/documents/teacherFeedback/feedbackConfig.ts`), but is oriented at
 * human input rather than LLM output and adds a repeatable `momentList` field
 * type for transcript moments.
 */
import {z} from 'zod';

import {getShadowMomentWindowIssue} from './shadowMomentWindow';

/** Field-value kinds a rubric field can capture. */
const SHADOW_FIELD_TYPES = ['text', 'momentList'] as const;

/** Allowed values for `ShadowRubricField.fieldType`. */
export const ShadowFieldTypeSchema = z.enum(SHADOW_FIELD_TYPES);

/** A single rubric field's value kind (`text` or `momentList`). */
export type ShadowFieldType = z.infer<typeof ShadowFieldTypeSchema>;

/** One transcript moment with a reviewer note. */
export const ShadowMomentSchema = z.object({
  /**
   * Inclusive start offset from the part's start (`MM:SS` or `H:MM:SS`). A
   * draft may hold anything; submit requires `getShadowMomentWindowIssue` to
   * pass, but documents written before that check may not.
   */
  start: z.string(),
  /** Inclusive end offset from the part's start; same rules as `start`. */
  end: z.string(),
  /** The reviewer's note about this moment. */
  note: z.string(),
  /**
   * 1-based recording part this moment falls in, for sections recorded in
   * multiple segments (a host rejoin) where each part's timestamps restart at
   * zero. Omitted for single-part recordings and for moments entered by hand.
   */
  part: z.number().int().positive().optional(),
  /**
   * When true, the reviewer nominated this moment as a "Teaching Star" moment
   * for other section leaders to watch. Once the feedback is moderation-
   * approved a nomination doc is created per highlighted moment (see
   * `sectionShadow/{courseId}/nominations`). Omitted/false ⇒ not nominated.
   */
  highlight: z.boolean().optional(),
});

/** A transcript moment with a reviewer note. */
export type ShadowMoment = z.infer<typeof ShadowMomentSchema>;

/**
 * Per-field stored value: freeform text (`text`) or a list of moments
 * (`momentList`).
 */
export const ShadowFieldValueSchema = z.union([
  z.string(),
  z.array(ShadowMomentSchema),
]);

/** Stored value for one feedback field. */
export type ShadowFieldValue = z.infer<typeof ShadowFieldValueSchema>;

/**
 * One rubric field descriptor. Drives the input form, the read-only renderers
 * (received feedback + moderation), and the submit-time validation
 * (`buildShadowContentSchema`), so adding or changing a field is a config edit
 * rather than a code change across those surfaces.
 */
export const ShadowRubricFieldSchema = z.object({
  /**
   * Stable machine key; `shadowFeedback.content` is keyed by this. Never reuse
   * a key for a different meaning — historical documents resolve by key.
   */
  key: z.string(),
  /** Human-readable label shown above the field. */
  label: z.string(),
  /** Helper/prompt text shown with the field; also fed to the LLM prompt. */
  description: z.string(),
  /** `text` = freeform string; `momentList` = repeatable transcript moments. */
  fieldType: ShadowFieldTypeSchema,
  /** When true, the field must be non-empty to submit. Omitted ⇒ optional. */
  required: z.boolean().optional(),
  /** When true (text only), render a multi-line textarea. Omitted ⇒ single line. */
  multiline: z.boolean().optional(),
});

/** A rubric field descriptor. */
export type ShadowRubricField = z.infer<typeof ShadowRubricFieldSchema>;

/** A versioned rubric: an ordered list of field descriptors. */
export const ShadowRubricSchema = z.object({
  /** Matches the key under which this rubric is registered in `SHADOW_RUBRICS`. */
  version: z.string(),
  /** Ordered field descriptors; render order follows array order. */
  fields: z.array(ShadowRubricFieldSchema),
});

/** A versioned shadowing feedback rubric. */
export type ShadowRubric = z.infer<typeof ShadowRubricSchema>;

/**
 * Append-only registry of rubric versions.
 *
 * NEVER mutate or remove a published version — historical `shadowFeedback`
 * documents resolve their fields by looking up their `rubricVersion` here. To
 * change the rubric, add a new version key and bump
 * `CURRENT_SHADOW_RUBRIC_VERSION`.
 */
export const SHADOW_RUBRICS: Record<string, ShadowRubric> = {
  v1: {
    version: 'v1',
    fields: [
      {
        key: 'timeManagement',
        label: 'Time management',
        description:
          'How well did the leader pace the section and manage time across activities?',
        fieldType: 'text',
        required: true,
        multiline: true,
      },
      {
        key: 'questioning',
        label: 'Questioning techniques',
        description:
          'How effectively did the leader use questions to check understanding and prompt thinking?',
        fieldType: 'text',
        required: true,
        multiline: true,
      },
      {
        key: 'engagement',
        label: 'Engagement',
        description:
          'How well did the leader involve students and sustain participation?',
        fieldType: 'text',
        required: true,
        multiline: true,
      },
      {
        key: 'encouragement',
        label: 'Encouragement & praise',
        description:
          'Close with specific encouragement and praise for the leader.',
        fieldType: 'text',
        required: true,
        multiline: true,
      },
      {
        key: 'moments',
        label: 'Moments',
        description: 'Call out specific moments the SL handled well.',
        fieldType: 'momentList',
        required: false,
      },
    ],
  },
};

/** The rubric version that new feedback is written against. */
export const CURRENT_SHADOW_RUBRIC_VERSION = 'v1';

/**
 * Returns the rubric for a given version.
 *
 * @param version - Rubric version key. Omitted ⇒ `CURRENT_SHADOW_RUBRIC_VERSION`.
 * @param courseId - Reserved for future per-course rubrics; currently ignored
 *   (the rubric is global). Omitted ⇒ no effect.
 * @returns The matching `ShadowRubric`.
 * @throws Error if `version` is not a known (append-only) rubric version.
 */
export function getShadowRubric({
  version = CURRENT_SHADOW_RUBRIC_VERSION,
}: {version?: string; courseId?: string} = {}): ShadowRubric {
  if (!(version in SHADOW_RUBRICS)) {
    throw new Error(`Unknown shadow rubric version: ${version}`);
  }
  return SHADOW_RUBRICS[version];
}

/**
 * Non-throwing built-in rubric lookup, used as a fallback when Firestore has no
 * rubric for a version yet (e.g. before the config rubric is seeded, or for
 * feedback stamped with a version that predates the Firestore store).
 *
 * @param version - Rubric version key.
 * @returns The built-in `ShadowRubric`, or undefined if `version` is unknown.
 */
export function getBuiltInShadowRubric(
  version: string,
): ShadowRubric | undefined {
  return SHADOW_RUBRICS[version];
}

/**
 * A moment as it must be on submit. Stored moments stay on the looser
 * `ShadowMomentSchema` so drafts and older documents still parse.
 */
const SubmittedShadowMomentSchema = ShadowMomentSchema.superRefine(
  (moment, context) => {
    const issue = getShadowMomentWindowIssue(moment);
    if (issue === undefined) return;
    context.addIssue({
      code: 'custom',
      message: issue,
      path: [issue === 'invalidStart' ? 'start' : 'end'],
    });
  },
);

/**
 * Builds a Zod schema that validates a feedback `content` record against a
 * rubric: required `text` fields must be non-empty strings; required
 * `momentList` fields must have at least one moment; every moment must have a
 * valid window (see `getShadowMomentWindowIssue`), with the issue reported at
 * the moment's `start` or `end` path; optional fields may be omitted. Unknown
 * keys are stripped.
 *
 * @param rubric - The rubric to derive the validator from.
 * @returns A Zod object schema keyed by rubric field key, suitable for
 *   validating `shadowFeedback.content` on submit (client and server).
 */
export function buildShadowContentSchema(rubric: ShadowRubric) {
  const shape: Record<string, z.ZodType> = {};
  for (const field of rubric.fields) {
    if (field.fieldType === 'momentList') {
      const moments = z.array(SubmittedShadowMomentSchema);
      shape[field.key] = field.required ? moments.min(1) : moments.optional();
    } else {
      const text = z.string();
      shape[field.key] = field.required ? text.min(1) : text.optional();
    }
  }
  return z.object(shape);
}
